import React, { useMemo, useState } from 'react';
import { Card, CardBody, CardHeader } from '../components/ui';
import { api } from '../utils/api';

type ExportKind = 'payments' | 'withdrawals';

function formatFilename(kind: ExportKind, from?: string, to?: string) {
  const base = kind === 'payments' ? 'payments' : 'withdrawals';
  if (from && to) return `${base}_${from}_to_${to}.xlsx`;
  if (from && !to) return `${base}_${from}.xlsx`;
  return `${base}_all.xlsx`;
}

function extractFilenameFromDisposition(disposition?: string): string | null {
  if (!disposition) return null;
  const m = disposition.match(/filename\*?=(?:UTF-8''|")?([^\";]+)"?/i);
  if (!m?.[1]) return null;
  try {
    return decodeURIComponent(m[1]);
  } catch {
    return m[1];
  }
}

const Operations: React.FC = () => {
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [allTime, setAllTime] = useState<boolean>(false);
  const [downloading, setDownloading] = useState<ExportKind | null>(null);
  const [job, setJob] = useState<{ id: string; kind: ExportKind; status: 'pending' | 'running' | 'done' | 'failed'; filename?: string; error?: string } | null>(null);

  const dateHint = useMemo(() => {
    if (allTime) return 'Будут выгружены все операции за всё время.';
    if (!from && !to) return 'Выберите дату/период или включите «За всё время».';
    if (from && !to) return 'Будут выгружены операции за выбранную дату.';
    if (!from && to) return 'Укажите дату “С”, либо очистите “По”.';
    return 'Будут выгружены операции за период.';
  }, [allTime, from, to]);

  const startExport = async (kind: ExportKind) => {
    try {
      setDownloading(kind);
      setJob({ id: 'creating', kind, status: 'pending' } as any);
      const create = await api.post('/admin/exports/jobs', {
        kind,
        from: allTime ? undefined : from || undefined,
        to: allTime ? undefined : to || undefined,
      });
      const jobId = create.data?.jobId as string;
      setJob({ id: jobId, kind, status: 'running' });

      const poll = async () => {
        const statusResp = await api.get(`/admin/exports/jobs/${jobId}`);
        const s = statusResp.data as any;
        setJob({
          id: jobId,
          kind,
          status: s.status,
          filename: s.filename,
          error: s.error,
        });
        if (s.status === 'done' || s.status === 'failed') return;
        setTimeout(poll, 1200);
      };
      poll();
    } catch (e: any) {
      setJob({ id: 'error', kind, status: 'failed', error: e?.response?.data?.error || 'Не удалось создать задачу экспорта' });
      alert(e?.response?.data?.error || 'Не удалось выгрузить файл');
    } finally {
      setDownloading(null);
    }
  };

  const downloadReady = async () => {
    if (!job || job.status !== 'done') return;
    try {
      setDownloading(job.kind);
      const response = await api.get(`/admin/exports/jobs/${job.id}/download`, { responseType: 'blob' });
      const disposition = response.headers?.['content-disposition'] as string | undefined;
      const filename =
        extractFilenameFromDisposition(disposition) ||
        job.filename ||
        formatFilename(job.kind, allTime ? undefined : from || undefined, allTime ? undefined : to || undefined);
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Не удалось скачать файл');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-8">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-[var(--app-heading)]">Работа с операциями</h1>
        <p className="text-sm text-[var(--app-text-muted)]">
          Выгрузка операций в Excel: отдельно платежи и отдельно выводы.
        </p>
      </div>

      <Card>
        <CardHeader
          title={<span className="text-lg font-semibold text-[var(--app-heading)]">Экспорт в Excel</span>}
          description="Выберите период (опционально) и скачайте отчёт."
        />
        <CardBody className="space-y-4">
          <div className="grid gap-3 md:grid-cols-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold uppercase text-secondary-500">Дата “С”</label>
              <input
                type="date"
                value={from}
                onChange={(e) => {
                  setAllTime(false);
                  setFrom(e.target.value);
                }}
                disabled={allTime}
                className="w-full rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface)] px-4 py-2 text-sm text-[var(--app-text)] outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold uppercase text-secondary-500">Дата “По”</label>
              <input
                type="date"
                value={to}
                onChange={(e) => {
                  setAllTime(false);
                  setTo(e.target.value);
                }}
                disabled={allTime}
                className="w-full rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface)] px-4 py-2 text-sm text-[var(--app-text)] outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              />
            </div>
            <div className="flex items-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setAllTime(false);
                  setFrom('');
                  setTo('');
                }}
                className="w-full rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface)] px-4 py-2 text-sm font-semibold text-[var(--app-text)] transition hover:bg-[var(--app-surface-subtle)]"
              >
                Очистить
              </button>
            </div>
          </div>

          <label className="flex items-center gap-2 rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface)] px-4 py-3 text-sm text-[var(--app-text)]">
            <input
              type="checkbox"
              checked={allTime}
              onChange={(e) => {
                const next = e.target.checked;
                setAllTime(next);
                if (next) {
                  setFrom('');
                  setTo('');
                }
              }}
              className="h-4 w-4 rounded border-[var(--app-border)]"
            />
            <span className="font-semibold">За всё время</span>
            <span className="text-[var(--app-text-muted)]">(без ограничения по датам)</span>
          </label>

          <div className="rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface-subtle)] px-4 py-3 text-sm text-[var(--app-text-muted)]">
            {dateHint}
          </div>

          {job ? (
            <div className="rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface)] px-4 py-3 text-sm text-[var(--app-text)]">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div className="text-[var(--app-text-muted)]">
                  Экспорт: <span className="font-semibold text-[var(--app-text)]">{job.kind === 'payments' ? 'Платежи' : 'Выводы'}</span> • Статус:{' '}
                  <span className="font-semibold text-[var(--app-text)]">{job.status}</span>
                  {job.error ? <span className="ml-2 text-danger">{job.error}</span> : null}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={downloadReady}
                    disabled={job.status !== 'done' || downloading !== null}
                    className="rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface)] px-4 py-2 text-sm font-semibold text-[var(--app-text)] transition hover:bg-[var(--app-surface-subtle)] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Скачать
                  </button>
                  <button
                    type="button"
                    onClick={() => setJob(null)}
                    className="rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface)] px-4 py-2 text-sm font-semibold text-[var(--app-text)] transition hover:bg-[var(--app-surface-subtle)]"
                  >
                    Скрыть
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          <div className="flex flex-col gap-3 md:flex-row">
            <button
              type="button"
              onClick={() => startExport('payments')}
              disabled={downloading !== null || (job?.status === 'running' || job?.status === 'pending')}
              className="inline-flex items-center justify-center rounded-2xl bg-primary-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {downloading === 'payments' ? 'Готовим…' : 'Запустить экспорт платежей'}
            </button>
            <button
              type="button"
              onClick={() => startExport('withdrawals')}
              disabled={downloading !== null || (job?.status === 'running' || job?.status === 'pending')}
              className="inline-flex items-center justify-center rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface)] px-5 py-3 text-sm font-semibold text-[var(--app-text)] shadow-sm transition hover:bg-[var(--app-surface-subtle)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {downloading === 'withdrawals' ? 'Готовим…' : 'Запустить экспорт выводов'}
            </button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default Operations;

